import { useParams } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { isValidProposalToken } from '@/mocks/template-config';
import { useProposals, getValidityStatus } from '@/hooks/useProposals';
import { useCompanyData } from '@/hooks/useCompanyData';
import { defaultTemplateConfig, replaceYears, getExperienceYears } from '@/mocks/template-config';
import { findPresetById, getCategories } from '@/mocks/template-presets';
import { applyPresetToConfig } from '@/hooks/useTemplatePresets';
import { showToast } from '@/components/Toast';
import ProposalSEO from '@/components/ProposalSEO';
import PDFExporter from './pdf/PDFExporter';
import type { Proposal, ProposalItem } from '@/hooks/useProposals';
import type { ProposalMock } from '@/mocks/proposals';

const UNIT_LABELS: Record<string, string> = {
  un: 'un',
  m: 'm',
  pc: 'pc',
  kit: 'kit',
  par: 'par',
  conj: 'conj',
  cx: 'cx',
  rol: 'rol',
  sv: 'sv',
};

function normalizeItems(items: ProposalItem[]): ProposalItem[] {
  return items.map((item) => ({
    ...item,
    unitType: (item as unknown as Record<string, string>).unit_type || item.unitType || 'un',
  }));
}

function adaptProposalToMock(proposal: Proposal): ProposalMock {
  const p = proposal as unknown as Record<string, unknown>;
  return {
    id: proposal.id,
    token: proposal.token,
    proposal_number: proposal.proposal_number,
    companyId: proposal.company_id || '',
    clientId: '',
    clientName: proposal.client_name,
    clientCompany: proposal.client_company,
    clientAddress: proposal.client_address,
    clientDocument: proposal.client_document,
    title: proposal.title,
    subtitle: proposal.subtitle,
    status: proposal.status,
    totalValue: proposal.total_value,
    validUntil: proposal.valid_until,
    items: normalizeItems(proposal.items),
    createdAt: proposal.created_at,
    updatedAt: proposal.updated_at,
    category: proposal.category,
    templatePresetId: proposal.template_preset_id,
    cover_image: proposal.cover_image,
    // extras para PDFSummary
    service_value: proposal.service_value,
    installation_value: proposal.installation_value,
    discount: proposal.discount,
    validity_days: proposal.validity_days,
    expires_at: proposal.expires_at,
    // extras para PDFVantagens
    advantages_text: proposal.advantages_text,
    advantages_image: proposal.advantages_image,
  } as unknown as ProposalMock;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Rascunho',
  sent: 'Enviada',
  viewed: 'Visualizada',
  approved: 'Aprovada',
  rejected: 'Recusada',
  revision: 'Alteração Solicitada',
};

export default function ProposalPage() {
  const { data: companyData } = useCompanyData();
  const { token } = useParams<{ token: string }>();
  const [showItems, setShowItems] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactReason, setContactReason] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [viewedMarked, setViewedMarked] = useState(false);

  const { getByToken, approve, markViewed } = useProposals();

  useEffect(() => {
    if (!token || !isValidProposalToken(token)) {
      setLoading(false);
      return;
    }
    getByToken(token).then((p) => {
      setProposal(p || null);
      setLoading(false);
    });
  }, [token, getByToken]);

  // Marcar como visualizada automaticamente quando o cliente abre
  // Aceita draft e sent — quando o cliente acessa, sempre vira "visualizada"
  useEffect(() => {
    if (proposal && (proposal.status === 'sent' || proposal.status === 'draft') && !viewedMarked) {
      setViewedMarked(true);
      markViewed(proposal.id).then((updated) => {
        if (updated) {
          setProposal(updated);
        }
      });
    }
  }, [proposal, markViewed, viewedMarked]);

  const handleApprove = async () => {
    if (!proposal) return;
    setApproving(true);
    try {
      const updated = await approve(proposal.id);
      if (updated) {
        setProposal(updated);
        showToast('Proposta aprovada com sucesso! Entraremos em contato para agendar a instalação.', 'success');
      } else {
        showToast('Não foi possível aprovar a proposta. Tente novamente.', 'error');
      }
    } catch {
      showToast('Erro ao aprovar proposta. Tente novamente.', 'error');
    } finally {
      setApproving(false);
    }
  };

  const handleSendRequest = () => {
    if (!contactReason) {
      showToast('Selecione o motivo da solicitação.', 'warning');
      return;
    }
    showToast('Solicitação enviada! Entraremos em contato em breve.', 'success');
    setShowContactModal(false);
    setContactReason('');
    setContactMessage('');
  };

  const pdfProposal = useMemo(() => {
    if (!proposal) return null;
    return adaptProposalToMock(proposal);
  }, [proposal]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center px-4">
        <div className="w-10 h-10 border-4 border-gray-700 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!token || !isValidProposalToken(token)) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full border-2 border-brand-gold/30 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-brand-gold text-2xl" />
          </div>
          <h1 className="text-white text-xl font-semibold mb-2">Proposta não encontrada</h1>
          <p className="text-brand-text-muted text-sm mb-4">
            O link da proposta pode estar expirado ou o código está incorreto.
          </p>
          <a
            href="/"
            className="inline-block bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-medium px-6 py-2 rounded-md transition-colors text-sm"
          >
            Voltar ao portal
          </a>
        </div>
      </div>
    );
  }

  if (!proposal) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full border-2 border-brand-gold/30 flex items-center justify-center mx-auto mb-4">
            <i className="ri-file-search-line text-brand-gold text-2xl" />
          </div>
          <h1 className="text-white text-xl font-semibold mb-2">Proposta não encontrada</h1>
          <p className="text-brand-text-muted text-sm mb-4">
            Esta proposta não existe ou foi removida.
          </p>
          <a
            href="/"
            className="inline-block bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-medium px-6 py-2 rounded-md transition-colors text-sm"
          >
            Voltar ao portal
          </a>
        </div>
      </div>
    );
  }

  // Aplica preset da proposta ao config
  const preset = proposal.template_preset_id ? findPresetById(proposal.template_preset_id) : undefined;
  const config = preset ? applyPresetToConfig(defaultTemplateConfig, preset) : defaultTemplateConfig;

  // Banner responsivo
  const bannerDesktop = config.top.bannerImage;
  const bannerMobile = preset?.topBannerMobile || bannerDesktop;

  const years = getExperienceYears(config.institutional.foundedYear);
  const yearsText = years >= 30 ? `mais de ${years} anos` : `${years} anos`;

  const activeSpecs = [...config.specialties]
    .filter((s) => s.active)
    .sort((a, b) => a.order - b.order);

  const visibleDifferentials = config.institutional.differentials.filter((d) => d.show);

  const validity = getValidityStatus(proposal);
  const formattedDate = proposal.expires_at
    ? new Date(proposal.expires_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;

  const categories = getCategories();

  // Format date helper
  const formatDateTime = (isoString: string | null | undefined) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formattedCreatedAt = formatDateTime(proposal.created_at);

  // Determina se o cliente pode aprovar (qualquer status que não seja já aprovado ou recusado)
  const canApprove = proposal.status !== 'approved' && proposal.status !== 'rejected';
  const isApproved = proposal.status === 'approved';

  return (
    <div className="min-h-screen bg-brand-dark font-sans">
      <ProposalSEO
        title={proposal.title}
        description={`Proposta comercial para ${proposal.client_name} - ${proposal.subtitle}`}
        companyName={companyData.name}
      />

      {/* Expired Banner */}
      {validity.isExpired && (
        <div className="bg-red-900/30 border-b border-red-500/30 py-3 px-4">
          <div className="max-w-5xl mx-auto flex items-center justify-center gap-2">
            <i className="ri-alert-line text-red-400 text-lg" />
            <p className="text-red-300 text-sm text-center">
              Esta proposta está vencida desde {formattedDate}.
            </p>
          </div>
        </div>
      )}

      {/* Expiring Soon Banner */}
      {validity.isExpiringSoon && (
        <div className="bg-yellow-900/30 border-b border-yellow-500/30 py-3 px-4">
          <div className="max-w-5xl mx-auto flex items-center justify-center gap-2">
            <i className="ri-time-line text-yellow-400 text-lg" />
            <p className="text-yellow-300 text-sm text-center">
              Esta proposta expira em {validity.daysLeft} {validity.daysLeft === 1 ? 'dia' : 'dias'} ({formattedDate}).
            </p>
          </div>
        </div>
      )}

      {/* ===== TOPO / CAPA ===== */}
      <div className="relative">
        <div className="absolute inset-0">
          <img
            src={bannerDesktop}
            alt="Capa da proposta"
            className="w-full h-full object-cover object-top hidden md:block"
          />
          <img
            src={bannerMobile}
            alt="Capa da proposta"
            className="w-full h-full object-cover object-top md:hidden"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/80 via-brand-dark/60 to-brand-dark/90" />
        </div>

        <div className="relative z-10 flex flex-col items-center pt-8 md:pt-12 px-4 pb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-brand-gold flex items-center justify-center">
              <div className="w-8 h-8 md:w-11 md:h-11 rounded-full border border-brand-gold flex items-center justify-center">
                <div className="w-3 h-3 md:w-4 md:h-4 bg-brand-gold rounded-full" />
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-white text-2xl md:text-3xl font-bold tracking-wide">
                Eletrônica <span className="text-brand-gold">2000</span>
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-16 md:w-24 h-px bg-brand-gold" />
            <span className="text-brand-gold text-xs md:text-sm tracking-[0.3em] uppercase font-medium">
              Desde 1993
            </span>
            <div className="w-16 md:w-24 h-px bg-brand-gold" />
          </div>

          <h1 className="font-serif text-white text-3xl md:text-5xl lg:text-6xl italic tracking-wide text-center mb-2">
            {proposal.title || `Proposta Comercial ${proposal.proposal_number}`}
          </h1>
          <p className="text-brand-gold text-sm md:text-lg tracking-wide text-center mb-6">
            {proposal.subtitle || proposal.description || config.top.subtitle}
          </p>
          <div className="w-32 md:w-48 h-px bg-brand-gold/50 mb-6" />
        </div>
      </div>

      {/* ===== CAIXA DO CLIENTE ===== */}
      <div className="max-w-5xl mx-auto px-4 -mt-2 mb-8">
        <div className="bg-brand-card/90 backdrop-blur-sm border border-brand-gold/30 rounded-lg p-6">
          <h3 className="text-brand-gold text-sm font-semibold tracking-wide mb-4">
            DADOS DO CLIENTE
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex justify-between md:justify-start md:gap-4">
              <span className="text-brand-text-muted text-sm">Cliente:</span>
              <span className="text-brand-text text-sm font-medium">{proposal.client_name}</span>
            </div>
            {proposal.client_company && (
              <div className="flex justify-between md:justify-start md:gap-4">
                <span className="text-brand-text-muted text-sm">Empresa:</span>
                <span className="text-brand-text text-sm font-medium">{proposal.client_company}</span>
              </div>
            )}
            <div className="flex justify-between md:justify-start md:gap-4">
              <span className="text-brand-text-muted text-sm">Proposta:</span>
              <span className="text-brand-text text-sm font-medium">{proposal.proposal_number}</span>
            </div>
            <div className="flex justify-between md:justify-start md:gap-4">
              <span className="text-brand-text-muted text-sm">Data:</span>
              <span className="text-brand-text text-sm font-medium">{formattedCreatedAt}</span>
            </div>
            <div className="flex justify-between md:justify-start md:gap-4">
              <span className="text-brand-text-muted text-sm">Validade:</span>
              <span className="text-brand-text text-sm font-medium">{proposal.valid_until}</span>
            </div>
            <div className="flex justify-between md:justify-start md:gap-4">
              <span className="text-brand-text-muted text-sm">Categoria:</span>
              <span className="text-brand-text text-sm font-medium">
                {categories.find(c => c.slug === proposal.category)?.name || proposal.category}
              </span>
            </div>
            {proposal.client_address && (
              <div className="flex justify-between md:justify-start md:gap-4">
                <span className="text-brand-text-muted text-sm">Endereço:</span>
                <span className="text-brand-text text-sm font-medium">{proposal.client_address}</span>
              </div>
            )}
            {proposal.client_document && (
              <div className="flex justify-between md:justify-start md:gap-4">
                <span className="text-brand-text-muted text-sm">CPF/CNPJ:</span>
                <span className="text-brand-text text-sm font-medium">{proposal.client_document}</span>
              </div>
            )}
            <div className="flex justify-between md:justify-start md:gap-4">
              <span className="text-brand-text-muted text-sm">Status:</span>
              <span className="text-brand-gold text-sm font-medium uppercase">
                {STATUS_LABELS[proposal.status] || proposal.status}
              </span>
            </div>
            {proposal.client_phone && (
              <div className="flex justify-between md:justify-start md:gap-4">
                <span className="text-brand-text-muted text-sm">Telefone:</span>
                <span className="text-brand-text text-sm font-medium">{proposal.client_phone}</span>
              </div>
            )}
            {proposal.client_email && (
              <div className="flex justify-between md:justify-start md:gap-4">
                <span className="text-brand-text-muted text-sm">E-mail:</span>
                <span className="text-brand-text text-sm font-medium">{proposal.client_email}</span>
              </div>
            )}
          </div>
          {proposal.description && (
            <div className="mt-4 pt-4 border-t border-brand-gold/10">
              <span className="text-brand-text-muted text-sm">Resumo:</span>
              <p className="text-brand-text text-sm mt-1">{proposal.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* ===== ITENS DA PROPOSTA ===== */}
      <div className="max-w-5xl mx-auto px-4 mb-8">
        <button
          onClick={() => setShowItems(!showItems)}
          className="w-full flex items-center justify-between bg-brand-card/50 border border-brand-gold/20 rounded-lg p-4 mb-4 hover:border-brand-gold/40 transition-colors cursor-pointer"
        >
          <span className="text-white font-semibold text-sm">Itens da Proposta</span>
          <i className={`ri-arrow-down-s-line text-brand-gold text-lg transition-transform ${showItems ? 'rotate-180' : ''}`} />
        </button>

        {showItems && (
          <div className="bg-brand-card/50 border border-brand-gold/20 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-brand-gold/20">
                    <th className="text-left text-brand-gold text-xs font-semibold p-4">Item</th>
                    <th className="text-center text-brand-gold text-xs font-semibold p-4">Un</th>
                    <th className="text-center text-brand-gold text-xs font-semibold p-4">Qtd</th>
                    <th className="text-right text-brand-gold text-xs font-semibold p-4">Unitário</th>
                    <th className="text-right text-brand-gold text-xs font-semibold p-4">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {normalizeItems(proposal.items).map((item, index) => (
                    <tr key={index} className="border-b border-brand-gold/10">
                      <td className="text-brand-text text-sm p-4">{item.description}</td>
                      <td className="text-brand-text text-sm text-center p-4">{UNIT_LABELS[item.unitType] || item.unitType}</td>
                      <td className="text-brand-text text-sm text-center p-4">{item.quantity}</td>
                      <td className="text-brand-text text-sm text-right p-4">
                        R$ {item.unitPrice.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="text-brand-text text-sm text-right p-4 font-medium">
                        R$ {item.total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-brand-gold/10">
                    <td colSpan={4} className="text-brand-text-muted text-sm p-4 text-right">
                      Subtotal:
                    </td>
                    <td className="text-brand-text text-sm p-4 text-right font-medium">
                      R$ {(proposal.subtotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                  {(proposal.service_value || 0) > 0 && (
                    <tr className="border-t border-brand-gold/5">
                      <td colSpan={4} className="text-brand-text-muted text-sm p-4 text-right">
                        Serviços:
                      </td>
                      <td className="text-brand-text text-sm p-4 text-right font-medium">
                        R$ {proposal.service_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  )}
                  {(proposal.installation_value || 0) > 0 && (
                    <tr className="border-t border-brand-gold/5">
                      <td colSpan={4} className="text-brand-text-muted text-sm p-4 text-right">
                        Instalação:
                      </td>
                      <td className="text-brand-text text-sm p-4 text-right font-medium">
                        R$ {proposal.installation_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  )}
                  {(proposal.discount || 0) > 0 && (
                    <tr className="border-t border-brand-gold/5">
                      <td colSpan={4} className="text-brand-text-muted text-sm p-4 text-right">
                        Desconto:
                      </td>
                      <td className="text-red-400 text-sm p-4 text-right font-medium">
                        - R$ {proposal.discount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  )}
                  <tr className="border-t border-brand-gold/30">
                    <td colSpan={4} className="text-brand-gold text-sm font-semibold p-4 text-right">
                      TOTAL:
                    </td>
                    <td className="text-brand-gold text-sm font-bold p-4 text-right">
                      R$ {proposal.total_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ===== AÇÕES ===== */}
      <div className="max-w-5xl mx-auto px-4 mb-12">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Botão Aprovar - aparece se a proposta NÃO estiver aprovada nem recusada */}
          {canApprove && (
            <button
              onClick={handleApprove}
              disabled={approving}
              className="flex-1 bg-green-600 hover:bg-green-500 disabled:bg-green-800 text-white font-semibold py-3 rounded-md flex items-center justify-center gap-2 transition-colors whitespace-nowrap cursor-pointer"
            >
              {approving ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <i className="ri-check-double-line" />
              )}
              {approving ? 'APROVANDO...' : 'APROVAR PROPOSTA'}
            </button>
          )}

          {/* Mensagem se já estiver aprovada */}
          {isApproved && (
            <div className="flex-1 bg-green-900/30 border border-green-500/30 text-green-400 font-semibold py-3 rounded-md flex items-center justify-center gap-2 whitespace-nowrap">
              <i className="ri-check-double-line" />
              PROPOSTA APROVADA
            </div>
          )}

          {/* Botão Solicitar Alterações - só aparece se não estiver aprovada */}
          {!isApproved && (
            <button
              onClick={() => setShowContactModal(true)}
              className="flex-1 bg-brand-card hover:bg-brand-card/80 border border-brand-gold/40 text-brand-gold font-semibold py-3 rounded-md flex items-center justify-center gap-2 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-edit-line" />
              SOLICITAR ALTERAÇÕES
            </button>
          )}

          <a
            href={`https://wa.me/${companyData.phone1.replace(/\D/g, '')}?text=Olá! Estou vendo a proposta ${proposal.proposal_number} e gostaria de conversar.`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 bg-green-700 hover:bg-green-600 text-white font-semibold py-3 rounded-md flex items-center justify-center gap-2 transition-colors whitespace-nowrap"
          >
            <i className="ri-whatsapp-line" />
            FALAR NO WHATSAPP
          </a>
          {pdfProposal && <PDFExporter proposal={pdfProposal} config={config} />}
        </div>
      </div>

      {/* ===== BLOCO INSTITUCIONAL ===== */}
      {config.institutional.show && (
        <div className="bg-brand-dark py-12 md:py-16 px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
              <div className="flex-1">
                <h2 className="text-white text-xl md:text-2xl font-semibold mb-6 text-center md:text-left">
                  {replaceYears(config.institutional.text, config.institutional.foundedYear)}
                </h2>
                {visibleDifferentials.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {visibleDifferentials.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-brand-gold/20 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-brand-gold text-xs" />
                        </div>
                        <span className="text-brand-text text-sm">
                          {replaceYears(item.text, config.institutional.foundedYear)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="hidden md:flex items-center justify-center flex-1">
                <div className="w-48 h-48 rounded-full border border-brand-gold/30 flex items-center justify-center opacity-40">
                  <div className="w-32 h-32 rounded-full border border-brand-gold/40 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-brand-gold/20 flex items-center justify-center">
                      <div className="w-6 h-6 bg-brand-gold/40 rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== RODAPÉ ===== */}
      <footer className="bg-brand-darker border-t border-brand-gold/10">
        <div className="max-w-5xl mx-auto px-4 py-8 md:py-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-4">
            {config.footer.qrCode.enabled && (
              <div className="flex items-center gap-4">
                <img
                  src={config.footer.qrCode.image}
                  alt="QR Code"
                  className="w-20 h-20 rounded-md object-cover"
                />
                <p className="text-brand-text-muted text-xs leading-relaxed">
                  Escaneie para acessar esta proposta a qualquer momento.
                </p>
              </div>
            )}

            <div className="space-y-2 min-w-0">
              <a
                href={`https://wa.me/${companyData.phone1.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-brand-gold transition-colors min-w-0"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-whatsapp-line text-brand-gold text-sm" />
                </div>
                <span className="text-brand-text text-sm break-words">{companyData.phone1}</span>
              </a>
              <a
                href={`https://wa.me/${companyData.phone2.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-brand-gold transition-colors min-w-0"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-whatsapp-line text-brand-gold text-sm" />
                </div>
                <span className="text-brand-text text-sm break-words">{companyData.phone2}</span>
              </a>
              <a
                href={`mailto:${companyData.email}`}
                className="flex items-center gap-2 hover:text-brand-gold transition-colors min-w-0"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-brand-gold text-sm" />
                </div>
                <span className="text-brand-text text-sm break-words">{companyData.email}</span>
              </a>
              <a
                href={`https://${companyData.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-brand-gold transition-colors min-w-0"
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-global-line text-brand-gold text-sm" />
                </div>
                <span className="text-brand-text text-sm break-words">{companyData.website}</span>
              </a>
            </div>

            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-map-pin-line text-brand-gold text-sm" />
                </div>
                <span className="text-brand-text text-sm">{companyData.city} – {companyData.state}</span>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-time-line text-brand-gold text-sm" />
                </div>
                <span className="text-brand-text text-sm">{companyData.workHours.weekday}</span>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                  <i className="ri-time-line text-brand-gold text-sm" />
                </div>
                <span className="text-brand-text text-sm">{companyData.workHours.saturday}</span>
              </div>
            </div>

            <div className="flex flex-col justify-center">
              <span className="text-brand-text-muted text-xs">CNPJ:</span>
              <span className="text-brand-text text-sm">{companyData.cnpj}</span>
            </div>
          </div>
        </div>

        {activeSpecs.length > 0 && (
          <div className="border-t border-brand-gold/10">
            <div className="max-w-5xl mx-auto px-4 py-4">
              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
                {activeSpecs.map((spec, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${spec.icon} text-brand-gold text-sm`} />
                    </div>
                    <span className="text-brand-text-muted text-xs uppercase tracking-wide">{spec.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-brand-gold/10">
          <div className="max-w-5xl mx-auto px-4 py-4 text-center">
            <p className="text-brand-gold text-sm">
              {companyData.name} – Tradição, tecnologia e confiança desde {companyData.foundedYear}.
            </p>
          </div>
        </div>
      </footer>

      {/* ===== MODAL DE CONTATO ===== */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setShowContactModal(false)} />
          <div className="relative bg-brand-card border border-brand-gold/30 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-brand-gold text-lg font-semibold mb-4">Solicitar Alterações</h3>
            <div className="space-y-4">
              <div>
                <label className="text-brand-text-muted text-xs mb-1 block">Motivo</label>
                <select
                  value={contactReason}
                  onChange={(e) => setContactReason(e.target.value)}
                  className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
                >
                  <option value="">Selecione...</option>
                  <option value="price">Ajustar valores</option>
                  <option value="items">Alterar itens</option>
                  <option value="quantity">Mudar quantidades</option>
                  <option value="deadline">Prazo de entrega</option>
                  <option value="other">Outro</option>
                </select>
              </div>
              <div>
                <label className="text-brand-text-muted text-xs mb-1 block">Mensagem</label>
                <textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  placeholder="Descreva as alterações desejadas..."
                  maxLength={500}
                  rows={4}
                  className="w-full bg-brand-dark/50 border border-brand-gold/20 rounded-md py-2 px-3 text-brand-text text-sm placeholder:text-brand-text-muted/60 focus:outline-none focus:border-brand-gold/50 resize-none"
                />
                <span className="text-brand-text-muted text-xs">{contactMessage.length}/500</span>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowContactModal(false)}
                  className="flex-1 border border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10 py-2 rounded-md text-sm transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendRequest}
                  className="flex-1 bg-brand-gold hover:bg-brand-gold-light text-brand-dark font-semibold py-2 rounded-md text-sm transition-colors cursor-pointer"
                >
                  Enviar Solicitação
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}